/**
 * TasksTab - Gestionare Sarcini/Tichete
 * Cu funcționalitate de editare de către admin
 */
import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDateRO } from "@/utils/dateFormat";
import { toast } from "sonner";
import { ClipboardList, Plus, MapPin, Edit2, Trash2, CheckCircle2, Play, Pause, X } from "lucide-react";
import { HelpTooltip } from "@/components/HelpTooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TasksTab({ tasks, taskStats, sites, workers, onRefresh, companyId }) {
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [newTask, setNewTask] = useState({ 
    title: "", 
    description: "", 
    site_id: "", 
    location_details: "", 
    priority: "normal", 
    assigned_to: [] 
  });
  
  // State pentru editare
  const [editTaskData, setEditTaskData] = useState({
    status: "",
    priority: "",
    assigned_to: [],
    resolution_notes: "",
    time_spent_minutes: 0
  });

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks`, newTask, { params: { company_id: companyId } });
      toast.success("Sarcină creată!");
      setNewTask({ title: "", description: "", site_id: "", location_details: "", priority: "normal", assigned_to: [] });
      setShowTaskDialog(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la creare");
    }
  };

  const handleOpenEditTask = (task) => {
    setEditingTask(task);
    setEditTaskData({
      status: task.status,
      priority: task.priority,
      assigned_to: task.assigned_to || [],
      resolution_notes: task.resolution_notes || "",
      time_spent_minutes: task.time_spent_minutes || 0
    });
  };

  const handleSaveTaskEdit = async () => {
    if (!editingTask) return;
    try {
      await axios.put(`${API}/tasks/${editingTask.id}`, editTaskData, { params: { company_id: companyId } });
      toast.success("Sarcină actualizată!");
      setEditingTask(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la actualizare");
    }
  };

  const handleQuickStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus }, { params: { company_id: companyId } });
      toast.success(`Status actualizat: ${statusLabels[newStatus]}`);
      onRefresh();
    } catch (error) {
      toast.error("Eroare la actualizare status");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm("Sigur doriți să ștergeți această sarcină?")) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { params: { company_id: companyId } });
      toast.success("Sarcină ștearsă!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const priorityColors = {
    low: "bg-slate-100 text-slate-700",
    normal: "bg-blue-100 text-blue-700",
    high: "bg-orange-100 text-orange-700",
    urgent: "bg-red-100 text-red-700"
  };

  const statusColors = {
    open: "bg-yellow-500",
    in_progress: "bg-blue-500",
    completed: "bg-green-500",
    cancelled: "bg-slate-400"
  };

  const statusLabels = {
    open: "Deschis",
    in_progress: "În lucru",
    completed: "Rezolvat",
    cancelled: "Anulat"
  };

  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <div>
          <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}} className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-indigo-600" />
            Sarcini / Tichete
            <HelpTooltip section="sarcini" />
          </CardTitle>
          <CardDescription>Gestionează sarcinile și problemele de pe șantiere</CardDescription>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-2 text-sm">
            <Badge variant="outline" className="bg-yellow-50">Deschise: {taskStats.open}</Badge>
            <Badge variant="outline" className="bg-blue-50">În lucru: {taskStats.in_progress}</Badge>
            <Badge variant="outline" className="bg-green-50">Rezolvate: {taskStats.completed}</Badge>
          </div>
          <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-task-button">
                <Plus className="w-4 h-4 mr-2" />
                Tichet Nou
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Creare Tichet Nou</DialogTitle>
                <DialogDescription>Adaugă o sarcină sau problemă nouă</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateTask} className="space-y-4">
                <div>
                  <Label>Titlu *</Label>
                  <Input
                    value={newTask.title}
                    onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                    placeholder="Ex: Reparație instalație electrică"
                    required
                  />
                </div>
                <div>
                  <Label>Descriere</Label>
                  <Textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                    placeholder="Detalii despre sarcină..."
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Proiect *</Label>
                    <Select value={newTask.site_id} onValueChange={(v) => setNewTask({...newTask, site_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Selectează" /></SelectTrigger>
                      <SelectContent>
                        {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Prioritate</Label>
                    <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Scăzută</SelectItem>
                        <SelectItem value="normal">Normală</SelectItem>
                        <SelectItem value="high">Ridicată</SelectItem>
                        <SelectItem value="urgent">URGENTĂ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Locație detaliată</Label>
                  <Input
                    value={newTask.location_details}
                    onChange={(e) => setNewTask({...newTask, location_details: e.target.value})}
                    placeholder="Ex: Etaj 2, apartament 5"
                  />
                </div>
                <div>
                  <Label>Asignare angajați</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
                    {workers.map(w => (
                      <label key={w.id} className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newTask.assigned_to.includes(w.id)}
                          onChange={(e) => {
                            const newAssigned = e.target.checked
                              ? [...newTask.assigned_to, w.id]
                              : newTask.assigned_to.filter(id => id !== w.id);
                            setNewTask({...newTask, assigned_to: newAssigned});
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{w.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                  Creează Tichet
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {tasks.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nu există tichete</p>
          ) : (
            tasks.map((task) => (
              <div 
                key={task.id} 
                className={`p-4 rounded-lg border ${
                  task.status === 'completed' ? 'bg-green-50 border-green-200' :
                  task.status === 'in_progress' ? 'bg-blue-50 border-blue-200' :
                  task.priority === 'urgent' ? 'bg-red-50 border-red-200' :
                  'bg-slate-50 border-slate-200'
                }`}
                data-testid={`task-card-${task.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-slate-500">#{task.ticket_number}</span>
                      <h4 className="font-semibold text-lg">{task.title}</h4>
                      <Badge className={statusColors[task.status]}>
                        {task.status === 'completed' && '✅'} {statusLabels[task.status]}
                      </Badge>
                      <Badge className={priorityColors[task.priority]}>
                        {task.priority === 'urgent' && '🔴'} 
                        {task.priority === 'low' ? 'Scăzută' : 
                         task.priority === 'normal' ? 'Normală' :
                         task.priority === 'high' ? 'Ridicată' : 'URGENTĂ'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{task.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {task.site_name} {task.location_details && `- ${task.location_details}`}
                      </span>
                      <span>👷 {task.assigned_names?.join(', ') || 'Neasignat'}</span>
                      <span>{formatDateRO(task.created_at)}</span>
                    </div>
                    {task.resolution_notes && (
                      <div className="mt-2 p-2 bg-green-100 rounded text-sm text-green-800">
                        <strong>Rezoluție:</strong> {task.resolution_notes}
                      </div>
                    )}
                  </div>
                  
                  {/* Butoane acțiuni */}
                  <div className="flex flex-col gap-2">
                    {/* Butoane schimbare rapidă status */}
                    <div className="flex gap-1">
                      {task.status === 'open' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuickStatusChange(task.id, 'in_progress')}
                          className="text-blue-600"
                          title="Începe lucrul"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      {task.status === 'in_progress' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleQuickStatusChange(task.id, 'completed')}
                            className="text-green-600"
                            title="Marchează rezolvat"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleQuickStatusChange(task.id, 'open')}
                            className="text-yellow-600"
                            title="Pune pe pauză"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {task.status === 'completed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuickStatusChange(task.id, 'in_progress')}
                          className="text-blue-600"
                          title="Redeschide"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Butoane editare/ștergere */}
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenEditTask(task)}
                        className="text-indigo-600"
                        title="Editează"
                        data-testid={`edit-task-${task.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteTask(task.id)}
                        className="text-red-600"
                        title="Șterge"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    {task.time_spent_minutes > 0 && (
                      <div className="text-right text-xs text-slate-500">
                        <p>{task.time_spent_minutes} min lucrat</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>

      {/* Dialog Editare Sarcină */}
      <Dialog open={!!editingTask} onOpenChange={(open) => !open && setEditingTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <Edit2 className="w-5 h-5" />
              Editează Sarcina
            </DialogTitle>
            <DialogDescription>
              #{editingTask?.ticket_number} - {editingTask?.title}
            </DialogDescription>
          </DialogHeader>
          {editingTask && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm"><strong>Proiect:</strong> {editingTask.site_name}</p>
                <p className="text-sm"><strong>Descriere:</strong> {editingTask.description || 'N/A'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={editTaskData.status} onValueChange={(v) => setEditTaskData({...editTaskData, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">🟡 Deschis</SelectItem>
                      <SelectItem value="in_progress">🔵 În lucru</SelectItem>
                      <SelectItem value="completed">🟢 Rezolvat</SelectItem>
                      <SelectItem value="cancelled">⚫ Anulat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Prioritate</Label>
                  <Select value={editTaskData.priority} onValueChange={(v) => setEditTaskData({...editTaskData, priority: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Scăzută</SelectItem>
                      <SelectItem value="normal">Normală</SelectItem>
                      <SelectItem value="high">Ridicată</SelectItem>
                      <SelectItem value="urgent">🔴 URGENTĂ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Timp lucrat (minute)</Label>
                <Input
                  type="number"
                  value={editTaskData.time_spent_minutes}
                  onChange={(e) => setEditTaskData({...editTaskData, time_spent_minutes: parseInt(e.target.value) || 0})}
                  min="0"
                />
              </div>
              
              <div>
                <Label>Asignare angajați</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-32 overflow-y-auto">
                  {workers.map(w => (
                    <label key={w.id} className="flex items-center gap-2 p-2 rounded border hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editTaskData.assigned_to.includes(w.id)}
                        onChange={(e) => {
                          const newAssigned = e.target.checked
                            ? [...editTaskData.assigned_to, w.id]
                            : editTaskData.assigned_to.filter(id => id !== w.id);
                          setEditTaskData({...editTaskData, assigned_to: newAssigned});
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{w.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Note rezoluție</Label>
                <Textarea
                  value={editTaskData.resolution_notes}
                  onChange={(e) => setEditTaskData({...editTaskData, resolution_notes: e.target.value})}
                  placeholder="Descrieți cum a fost rezolvată problema..."
                  rows={3}
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSaveTaskEdit} className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                  Salvează Modificările
                </Button>
                <Button variant="outline" onClick={() => setEditingTask(null)} className="flex-1">
                  Anulează
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
